import { getArtworks } from "../api/artwork";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { SERVER_URL } from "../api";
import {
  PictureAsPdf,
  Description,
  InsertDriveFile,
  TableChart,
} from "@material-ui/icons";
import React from "react";

export const getFileIconByExtension = (file) => {
  const name = typeof file === "string" ? file : file.name;
  const extension = name
    .split(".")
    .pop()
    .toLowerCase();

  switch (extension) {
    case "pdf":
      return <PictureAsPdf style={{ fontSize: 40 }} color="error" />;
    case "doc":
    case "docx":
      return <Description style={{ fontSize: 40 }} color="primary" />;
    case "xls":
    case "xlsx":
      return <TableChart style={{ fontSize: 40 }} color="success" />;
    default:
      return <InsertDriveFile style={{ fontSize: 40 }} color="disabled" />;
  }
};

export const downloadFilesAsZip = async (
  fileUrls,
  zipName = "archivos.zip"
) => {
  try {
    const zip = new JSZip();

    // Procesar cada archivo
    const promises = fileUrls.map(async (fileUrl, index) => {
      try {
        let fileName = fileUrl.split("/").pop() || `archivo_${index + 1}`;
        fileName = fileName.replace(/^\d+-/, "");

        const response = await fetch(`${SERVER_URL}/${fileUrl}`);

        if (!response.ok) {
          console.warn(`No se pudo descargar: ${fileUrl} (${response.status})`);
          return;
        }

        const blob = await response.blob();

        const extension = fileName
          .split(".")
          .pop()
          ?.toLowerCase();
        let folder = zip;

        if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)) {
          folder = zip.folder("images");
        } else if (["mp4", "avi", "mov", "mkv", "wmv"].includes(extension)) {
          folder = zip.folder("videos");
        } else if (["doc", "docx", "pdf", "txt", "rtf"].includes(extension)) {
          folder = zip.folder("files");
        } else if (["mp3", "wav", "flac", "aac"].includes(extension)) {
          folder = zip.folder("audio");
        } else {
          folder = zip.folder("otros");
        }

        // Añadir al ZIP
        folder.file(fileName, blob);
      } catch (error) {
        console.log(`Error al procesar ${fileUrl}:`, error);
      }
    });
    await Promise.all(promises);

    const content = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: {
        level: 6,
      },
    });
    saveAs(content, zipName);
    console.log("Descarga completada");
  } catch (error) {
    console.error("Error al crear el ZIP:", error);
    alert(
      "Error al descargar los archivos. Revisa la consola para más detalles."
    );
  }
};

export function getKeyByValue(object, value) {
  return Object.keys(object).find((key) => object[key] === value);
}

export const delay = (n) => new Promise((r) => setTimeout(r, n * 1000));

export const getNonEmpty = (field) => {
  const defaultLanguage = "es";
  if (!field || !Object.keys(field)?.length) return "----";
  if (field[defaultLanguage] && field[defaultLanguage] !== "")
    return field[defaultLanguage];
  for (const lang of Object.keys(field)) {
    if (field[lang] && field[lang] !== "") return field[lang];
  }
  return "----";
};

export const checkIsEmpty = (field) => {
  return Object.values(field).every((x) => x === null || x === "" || x === " ");
};

export const getFileType = (fileName) => {
  if (!fileName) return "unknown";
  const ext = fileName
    .split(".")
    .pop()
    .toLowerCase();
  switch (ext) {
    case "pdf":
      return "file";
    case "m4a":
      return "audio";
    case "mp3":
      return "audio";
    case "mpa":
      return "audio";
    case "wma":
      return "audio";
    case "aif":
      return "audio";
    case "cda":
      return "audio";
    case "mid":
      return "audio";
    case "midi":
      return "audio";
    case "ogg":
      return "audio";
    case "wav":
      return "audio";
    case "wpl":
      return "audio";
    case "jpg":
      return "image";
    case "jpeg":
      return "image";
    case "png":
      return "image";
    case "svg":
      return "image";
    case "gif":
      return "image";
    case "avi":
      return "video";
    case "m4v":
      return "video";
    case "mkw":
      return "video";
    case "mp4":
      return "video";
    case "wmv":
      return "video";
    case "mov":
      return "video";
    default:
      return "unknown";
  }
};

function areWordsSimilar(word1, word2, threshold = 2) {
  if (word1 === "Untitled" || word2 === "Untitled") {
    return false;
  }

  const matrix = Array.from({ length: word1.length + 1 }, (_, i) =>
    Array.from({ length: word2.length + 1 }, (_, j) =>
      i === 0 ? j : j === 0 ? i : 0
    )
  );

  for (let i = 1; i <= word1.length; i++) {
    for (let j = 1; j <= word2.length; j++) {
      if (word1[i - 1] === word2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + 1
        );
      }
    }
  }

  const levenshteinDistance = matrix[word1.length][word2.length];

  return levenshteinDistance <= threshold;
}

export const findSimilarArtworks = ({ artworks, artwork }) => {
  const result = artworks?.filter(
    (x) =>
      x?._id !== artwork?._id &&
      areWordsSimilar(x?.title, artwork?.title) &&
      x.artworkType?._id === artwork?.artworkType &&
      x.artist?._id === artwork?.artist &&
      x.year === artwork.year
  );
  return result;
};

export const saveFiltersToSession = (pageKey, filters) => {
  sessionStorage.setItem(`filters_${pageKey}`, JSON.stringify(filters));
};

export const getFiltersFromSession = (pageKey, defaultFilters) => {
  const stored = sessionStorage.getItem(`filters_${pageKey}`);
  return stored ? JSON.parse(stored) : defaultFilters;
};

export const clearFiltersFromSession = (pageKey) => {
  sessionStorage.removeItem(`filters_${pageKey}`);
};

export function getNameOwner(owners, listaOwner) {
  if (!listaOwner || listaOwner.length <= 0) return null;
  const ids = listaOwner.map((x) => x.owner || x.supplier);
  const nameList = owners
    .filter((owner) => ids.includes(owner._id)) // filtramos solo los dueños con id en ids
    .map((owner) => owner.fullName)
    .filter((name) => name !== "");
  return nameList.join(", ");
}

export function getNameSupplier(suppliers, listaSuppliers) {
  if (!listaSuppliers || listaSuppliers.length <= 0) return null;
  const id = listaSuppliers.find((x) => x.ss)?.supplier;
  const name = suppliers.find((x) => x._id === id)?.fullName;
  return name;
}
